import React, { useState, useEffect } from 'react';
import { getConfig } from '../services/configApi';
import { retryJob } from '../services/api';

/**
 * RetryPanel Component
 *
 * Renders a "Retry Job" button with a destination press dropdown.
 * Fetches active presses from GET /config, pre-selects the original press,
 * and calls POST /jobs/{jobId}/retry on confirm.
 *
 * Requirements: 2.1, 5.1
 */

export interface RetryPanelProps {
  jobId: string;
  originalPress: string;
  onRetried: () => void;
}

export const RetryPanel: React.FC<RetryPanelProps> = ({ jobId, originalPress, onRetried }) => {
  const [presses, setPresses] = useState<string[]>([]);
  const [selectedPress, setSelectedPress] = useState<string>(originalPress);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingPresses, setLoadingPresses] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const config = await getConfig();
        if (!cancelled) {
          const activePresses = Object.entries(config.presses)
            .filter(([, cfg]) => cfg.status === 'active')
            .map(([name]) => name)
            .sort((a, b) => a.localeCompare(b));
          setPresses(activePresses);
          if (activePresses.includes(originalPress)) {
            setSelectedPress(originalPress);
          } else if (activePresses.length > 0) {
            setSelectedPress(activePresses[0]);
          }
        }
      } catch {
        if (!cancelled) {
          setError('Failed to load press list');
        }
      } finally {
        if (!cancelled) {
          setLoadingPresses(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [originalPress]);

  const handleRetry = async () => {
    setLoading(true);
    setError(null);
    try {
      await retryJob(jobId, selectedPress);
      onRetried();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to retry job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="retry-panel" className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Retry Job</h3>

      <div className="flex items-center gap-3">
        <label htmlFor="retry-destination-press" className="text-sm text-gray-600">
          Destination:
        </label>
        <select
          id="retry-destination-press"
          data-testid="retry-destination-press-select"
          value={selectedPress}
          onChange={(e) => setSelectedPress(e.target.value)}
          disabled={loading || loadingPresses}
          className="text-sm border border-gray-300 rounded px-2 py-1"
        >
          {loadingPresses ? (
            <option>Loading...</option>
          ) : (
            presses.map((press) => (
              <option key={press} value={press}>
                {press}
              </option>
            ))
          )}
        </select>

        <button
          data-testid="retry-button"
          onClick={handleRetry}
          disabled={loading || loadingPresses || presses.length === 0}
          className={`px-4 py-1.5 text-sm font-medium rounded ${
            loading || loadingPresses || presses.length === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <span
                data-testid="retry-loading"
                className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"
              />
              Retrying...
            </span>
          ) : (
            'Retry Job'
          )}
        </button>
      </div>

      {error && (
        <p data-testid="retry-error" className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

export default RetryPanel;
